"use client";

import { ReactNode, Suspense } from "react";
import dynamic from "next/dynamic";
import { Toaster } from "react-hot-toast";
import { ThemeProvider } from "@npm_leadtech/qr-ui-components";

import { StoreUtms } from "@/components/StoreUtms/StoreUtms";
import { StorePsp } from "@/components/StorePsp";
import { StoreMid } from "@/components/StoreMid";
import { HistoryEvent } from "@/components/HistoryEvent";
//import { DevRoleSelector } from "@/components/DevRoleSelector";
import { AmplitudeInitializer } from "@/components/AmplitudeInitializer/AmplitudeInitializer";

import { LayoutProvider } from "@/contexts/LayoutContext";
import { UserProvider } from "@/contexts/UserContext";
import { GlobalProvider } from "@/contexts/GlobalContext/GlobalContext";
import { QREditorProvider } from "@/contexts/QREditorContext";
import { AmplitudeContextProvider } from "@/contexts/AmplitudeContext/AmplitudeContext";
import { DatalayerProvider } from "@/contexts/DataLayerEventContext";
import { ABTestingProvider } from "@/contexts/ABTestingContext/ABTestingContext";
import { ThirdPartyScripts } from "./ThirdPartyScripts";
import { initializePreviewRegistry } from "@/features/previews/registry/initialize";

const DynamicLoadingBarProvider = dynamic(() =>
  import("@/contexts/LoadingBarContext/LoadingBarContext").then(
    ({ LoadingBarProvider }) => LoadingBarProvider
  )
);

const DynamicModalProvider = dynamic(() =>
  import("@/contexts/ModalContext").then(({ ModalProvider }) => ModalProvider)
);

const OptionalProvidersWrapper = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<>{children}</>}>
    <DynamicLoadingBarProvider>
      <DynamicModalProvider>{children}</DynamicModalProvider>
    </DynamicLoadingBarProvider>
  </Suspense>
);

import { NAMESPACES } from "@/internals/constants/namespaces";
import { usePrefetchRouter } from "@/hooks/navigation/usePrefetchRouter";
import { TranslationsProvider } from "@/hocs/TranslationsProvider";
import { getSiteDomain } from "@/utils/site";
import { getGTMId } from "@/utils/gtm";
import { THEME } from "@/themes/types";

initializePreviewRegistry();

const currentTheme: THEME = process.env.NEXT_PUBLIC_THEME as THEME;

export const LayoutWrapper = ({
  children,
  lang,
  genericErrors,
  modalData,
  resources,
}: {
  children: ReactNode;
  lang?: string;
  genericErrors: any;
  modalData?: any;
  resources?: any;
}) => {
  usePrefetchRouter([]);

  const siteDomain = getSiteDomain();
  const gtmId = getGTMId(siteDomain);

  return (
    <>
      <HistoryEvent />
      <ThirdPartyScripts gtmId={gtmId} />
      <ABTestingProvider>
        <TranslationsProvider
          locale={lang || "en-US"}
          resources={resources}
          namespaces={[NAMESPACES.GENERIC_ERRORS]}
        >
          <GlobalProvider>
            <AmplitudeContextProvider>
              <ThemeProvider theme={currentTheme}>
                <LayoutProvider
                  modalData={modalData}
                  genericErrors={genericErrors}
                >
                  <UserProvider>
                    <AmplitudeInitializer />
                    <DatalayerProvider>
                      <QREditorProvider>
                        <OptionalProvidersWrapper>
                          <Suspense>
                            <StoreUtms />
                            <StorePsp />
                            <StoreMid />
                          </Suspense>
                          {children}
                          {/*   <DevRoleSelector /> */}
                        </OptionalProvidersWrapper>
                      </QREditorProvider>
                    </DatalayerProvider>
                  </UserProvider>
                </LayoutProvider>
              </ThemeProvider>
            </AmplitudeContextProvider>
          </GlobalProvider>

          <Toaster />
        </TranslationsProvider>
      </ABTestingProvider>
    </>
  );
};
